import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocalState, useOnline } from '@ndrc/app-sdk/client';
import { useDesk } from '../desk/context';
import { ApiError, callCore, mutateCore } from '../lib/rpc';
import { t } from '../messages';
import { CommandState } from './MissionValidations';

export function MissionPreviewResponse({
  classId,
  runId,
  blockId,
  version,
  kind,
  enabled,
  isolatedChoice = false,
}: {
  classId: string;
  runId: string;
  blockId: string;
  version: number;
  kind: 'ack' | 'submit' | 'self_declared' | 'teacherValidate';
  enabled: boolean;
  isolatedChoice?: boolean;
}) {
  const { me } = useDesk();
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft, stored] = useLocalState(
    `preview-response:${me.id}:${runId}:${blockId}${isolatedChoice ? `:${kind}` : ''}`,
    {
      text: '',
      version,
      key: crypto.randomUUID(),
      frozen: false,
      commandId: null as string | null,
    },
  );
  const receipt = useQuery({
    queryKey: ['core', 'commands.status', classId, draft.commandId],
    queryFn: () =>
      callCore<{ status: string }>('commands.status', { classId, commandId: draft.commandId }),
    enabled: !!draft.commandId,
    retry: false,
    refetchInterval: (query) =>
      ['ok', 'failed', 'partial'].includes(query.state.data?.status ?? '') ? false : 2000,
  });
  const failed = ['failed', 'partial'].includes(receipt.data?.status ?? '');
  const conflict = draft.version !== version;
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        setError('');
        setDraft({ ...draft, frozen: true });
        void mutateCore<{ commandId: string }>(
          'missions.previewRespond',
          {
            classId,
            runId,
            blockId,
            expectedVersion: draft.version,
            kind,
            ...(kind === 'submit' ? { text: draft.text } : {}),
          },
          draft.key,
        )
          .then((result) => setDraft({ ...draft, frozen: true, commandId: result.commandId }))
          .catch((e: unknown) => {
            setError(e instanceof Error ? e.message : t('mission.student.error'));
            if (e instanceof ApiError && e.status >= 400 && e.status < 500)
              setDraft({ ...draft, frozen: false });
          })
          .finally(() => setBusy(false));
      }}
    >
      {kind === 'submit' && (
        <label className="ndrc-field">
          {t('mission.response.text')}
          <textarea
            className="ndrc-input"
            required
            maxLength={6000}
            value={draft.text}
            disabled={busy || draft.frozen}
            onChange={(event) => setDraft({ ...draft, text: event.target.value })}
          />
        </label>
      )}
      <p className="ndrc-hint">{t('mission.response.local')}</p>
      {!stored && <p role="alert">{t('teacher.missions.storageError')}</p>}
      {error && <p role="alert">{error}</p>}
      {conflict && !draft.commandId && <p role="alert">{t('mission.response.conflict')}</p>}
      {draft.commandId && <CommandState classId={classId} commandId={draft.commandId} />}
      <button
        className="ndrc-button"
        type="submit"
        disabled={
          !online ||
          !enabled ||
          busy ||
          conflict ||
          !!draft.commandId ||
          (kind === 'submit' && !draft.text.trim())
        }
      >
        {t(
          kind === 'teacherValidate'
            ? 'teacher.preview.validate'
            : kind === 'ack'
              ? 'mission.response.ack'
              : kind === 'submit'
                ? 'mission.response.send'
                : 'mission.response.declare',
        )}
      </button>
      {(failed || (!draft.commandId && (conflict || error))) && (
        <button
          className="ndrc-button"
          type="button"
          disabled={busy || !online || (draft.frozen && !draft.commandId && !conflict)}
          onClick={() => {
            setDraft({
              ...draft,
              version,
              frozen: false,
              commandId: null,
              key: crypto.randomUUID(),
            });
            setError('');
          }}
        >
          {t('mission.response.resume')}
        </button>
      )}
    </form>
  );
}
